import { X, Download, CheckCircle, AlertTriangle, BarChart3 } from 'lucide-react';

// ─── Helpers ──────────────────────────────────────────────────────────────────
const scoreBarColor = (s) =>
  s >= 80 ? 'bg-emerald-500' : s >= 60 ? 'bg-[#2563eb]' : s >= 40 ? 'bg-amber-500' : 'bg-red-500';

const scoreTextColor = (s) =>
  s >= 80 ? 'text-emerald-700' : s >= 60 ? 'text-[#2563eb]' : s >= 40 ? 'text-amber-700' : 'text-red-600';

const heroBg = (s) =>
  s >= 80 ? 'bg-emerald-50 border-emerald-200' :
  s >= 60 ? 'bg-blue-50 border-blue-200' :
  s >= 40 ? 'bg-amber-50 border-amber-200' : 'bg-red-50 border-red-200';

const matchBadge = {
  Strong:  'bg-emerald-100 text-emerald-700 border-emerald-200',
  Partial: 'bg-amber-100 text-amber-700 border-amber-200',
  Missing: 'bg-red-100 text-red-600 border-red-200',
};

// Defensive helper — always returns a number or null, never an object
const normalizeScore = (value) =>
  typeof value === 'object' && value !== null
    ? (typeof value.score === 'number' ? value.score : null)
    : (typeof value === 'number' ? value : null);

// Normalize dimension score across linkedin (dim.score) and resume (dim.raw_score) formats
function dimScore(dim) { return dim.raw_score ?? dim.score ?? 0; }

// ─── Export PDF ───────────────────────────────────────────────────────────────
function buildPrintHTML(candidate, source, scoreData) {
  const bd = scoreData?.breakdown || {};
  const score = scoreData?.score ?? 0;
  const dims = bd.dimensions || [];
  const skillTable = scoreData?.skill_level_table || [];
  const sourceLabel = source === 'linkedin' ? 'LinkedIn Score Report' : 'Resume Score Report';
  return `<html><head><title>${sourceLabel} - ${candidate.name}</title>
<style>
  body{font-family:Georgia,serif;padding:40px;max-width:800px;margin:0 auto;color:#1e293b}
  h1{color:#1e293b;border-bottom:2px solid #2563eb;padding-bottom:10px}
  h2{color:#2563eb;margin-top:24px;font-size:16px}
  table{width:100%;border-collapse:collapse;margin:12px 0;font-size:13px}
  th,td{border:1px solid #e2e8f0;padding:8px 10px;text-align:left}
  th{background:#f8fafc;color:#475569}
  .score-box{background:#eff6ff;border:1px solid #bfdbfe;border-radius:8px;padding:16px;margin:16px 0}
  ul{margin:6px 0;padding-left:18px}li{margin:3px 0;font-size:13px;color:#475569}
</style></head><body>
<h1>${sourceLabel}</h1>
<p><strong>Candidate:</strong> ${candidate.name} &nbsp;|&nbsp; <strong>Date:</strong> ${new Date().toLocaleDateString()}</p>
<div class="score-box"><strong style="font-size:32px">${score}</strong><span style="color:#64748b">/100</span>&nbsp;&nbsp;
<span style="font-style:italic;color:#475569">${bd.oneliner || ''}</span></div>
${dims.length ? `<h2>Dimension Breakdown</h2><table><tr><th>Dimension</th><th>Weight</th><th>Score</th></tr>${dims.map(d=>`<tr><td>${d.name}</td><td>${d.weight}%</td><td>${dimScore(d)}</td></tr>`).join('')}</table>` : ''}
${skillTable.length ? `<h2>Skill Level Table</h2><table><tr><th>Skill</th><th>Required</th><th>Candidate</th><th>Match</th><th>Score</th></tr>${skillTable.map(s=>`<tr><td>${s.skill}</td><td>${s.required||'-'}</td><td>${s.candidate||'-'}</td><td>${s.match_type||'-'}</td><td>${s.score||0}</td></tr>`).join('')}</table>` : ''}
<h2>Strengths</h2><ul>${(bd.strengths||[]).map(s=>`<li>${s}</li>`).join('')}</ul>
<h2>Concerns</h2><ul>${(bd.concerns||[]).map(c=>`<li>${c}</li>`).join('')}</ul>
</body></html>`;
}

// ─── Main Modal ───────────────────────────────────────────────────────────────
/**
 * V4ScoreReportModal — unified modal for linkedin_score and resume_score (new v4 schema).
 * Props: candidate (full object), source ("linkedin"|"resume"), onClose (fn)
 */
export default function V4ScoreReportModal({ candidate, source, onClose }) {
  // ── Shape-agnostic normalization: handles legacy flat AND new nested shapes ──
  let scoreObj     = null;
  let numericScore = null;
  let breakdown    = null;
  let summary      = '';
  let flags        = [];
  let strengths    = [];
  let concerns     = [];

  if (source === 'linkedin') {
    const raw = candidate?.linkedin_score || candidate?.linkedinscore || null;
    if (raw) {
      if (typeof raw.score === 'number' && raw.breakdown) {
        // New nested shape: { score, breakdown:{dimensions,...} }
        numericScore = raw.score;
        breakdown    = raw.breakdown;
      } else if (typeof raw.total_score === 'number') {
        // Legacy flat shape: { total_score, dimensions, flags, ... }
        numericScore = Math.round(raw.total_score);
        breakdown    = raw;
      }
      scoreObj  = raw;
      summary   = breakdown?.oneliner  || raw.summary        || raw.recommendation || '';
      flags     = breakdown?.flags     || raw.flags          || [];
      strengths = breakdown?.strengths || raw.strengths      || [];
      concerns  = breakdown?.concerns  || raw.concerns       || [];
    }
  } else if (source === 'resume') {
    const raw = candidate?.resume_score || candidate?.resumescore || null;
    if (raw) {
      if (typeof raw.score === 'number') {
        numericScore = raw.score;
        breakdown    = raw.breakdown || raw;
      } else if (typeof raw === 'number') {
        numericScore = raw;
      }
      scoreObj  = raw;
      summary   = breakdown?.summary   || raw.summary        || '';
      flags     = breakdown?.flags     || raw.flags          || [];
      strengths = breakdown?.strengths || raw.strengths      || [];
      concerns  = breakdown?.concerns  || raw.concerns       || [];
    }
  }

  if (!scoreObj) return null;

  // Dimensions from either shape
  const dimensions = breakdown?.dimensions || scoreObj?.dimensions || [];

  const score      = normalizeScore(numericScore);
  const bd         = breakdown || {};
  const dims       = dimensions;
  const skillTable = scoreObj?.skill_level_table || bd.skill_level_table || [];
  const intFocus   = bd.interview_focus || [];
  const oneliner   = summary;
  const rec        = scoreObj?.recommendation || bd.recommendation || '';
  const sourceLabel = source === 'linkedin' ? 'LinkedIn Score' : 'Resume Score';

  const handleExport = () => {
    const html = buildPrintHTML(candidate, source, scoreObj);
    const blob = new Blob([html], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const w = window.open(url, '_blank');
    if (w) {
      // Use timeout fallback in case load event fires before listener attaches
      const doPrint = () => { w.print(); URL.revokeObjectURL(url); };
      w.addEventListener('load', doPrint);
      setTimeout(() => { URL.revokeObjectURL(url); }, 10000); // safety cleanup
    } else {
      URL.revokeObjectURL(url);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose}
      style={{ fontFamily: 'Georgia, serif' }}
    >
      <div
        className="bg-white border border-slate-200 rounded-2xl w-full max-w-4xl max-h-[92vh] overflow-hidden flex flex-col shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        data-testid="v4-score-report-modal"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 bg-white">
          <div>
            <h2 className="text-lg font-bold text-slate-800">{candidate.name}</h2>
            <p className="text-slate-400 text-xs mt-0.5">{sourceLabel}</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleExport}
              className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium bg-slate-100 text-slate-600 hover:bg-slate-200 border border-slate-200 transition-colors"
              data-testid="v4-export-pdf-btn"
            >
              <Download className="w-3.5 h-3.5" /> Export PDF
            </button>
            <button onClick={onClose} className="p-2 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-lg">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Scrollable body */}
        <div className="flex-1 overflow-auto bg-slate-50 p-6 space-y-5">

          {/* Score hero */}
          <div className={`rounded-2xl border p-5 ${heroBg(score)}`} data-testid="v4-score-hero">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-5">
                <div className="text-center">
                  <p className={`text-5xl font-bold ${scoreTextColor(score)}`}>{score}</p>
                  <p className="text-xs text-slate-500 mt-0.5">out of 100</p>
                </div>
                {oneliner && (
                  <>
                    <div className="h-12 w-px bg-slate-200" />
                    <p className="text-sm text-slate-600 max-w-md leading-relaxed">{oneliner}</p>
                  </>
                )}
              </div>
              {rec && (
                <span className={`text-xs font-semibold px-3 py-1 rounded-full border ${
                  rec.toLowerCase().includes('shortlist') ? 'bg-emerald-100 text-emerald-700 border-emerald-200' :
                  rec.toLowerCase().includes('consider')  ? 'bg-amber-100 text-amber-700 border-amber-200' :
                                                             'bg-red-100 text-red-600 border-red-200'
                }`}>{rec}</span>
              )}
            </div>
            <div className="w-full bg-white/70 rounded-full h-2.5">
              <div className={`h-2.5 rounded-full transition-all duration-700 ${scoreBarColor(score)}`}
                   style={{ width: `${score}%` }} />
            </div>
          </div>

          {/* Dimension breakdown */}
          {dims.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-slate-700 mb-3">Dimension Breakdown</h3>
              <div className="grid md:grid-cols-2 gap-3">
                {dims.map((dim) => {
                  const ds = dimScore(dim);
                  const ws = dim.weighted_score;
                  return (
                    <div key={dim.name} className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm"
                         data-testid={`v4-dim-${dim.name}`}>
                      <div className="flex justify-between items-baseline mb-1.5">
                        <span className="text-xs font-semibold text-slate-700 capitalize">{dim.name.replace(/_/g,' ')}</span>
                        <div className="text-right">
                          <span className={`text-xl font-bold ${scoreTextColor(ds)}`}>{ds}</span>
                          <span className="text-xs text-slate-400 ml-0.5">/100</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 mb-2">
                        <div className="flex-1 bg-slate-100 rounded-full h-1.5">
                          <div className={`h-1.5 rounded-full ${scoreBarColor(ds)}`}
                               style={{ width: `${ds}%` }} />
                        </div>
                        <span className="text-[10px] text-slate-400 w-14 text-right">
                          {dim.weight}%{ws != null ? ` → ${ws.toFixed(1)}pts` : ''}
                        </span>
                      </div>
                      <ul className="space-y-0.5">
                        {(dim.evidence || []).slice(0, 3).map((ev, j) => (
                          <li key={j} className="text-[11px] text-slate-500 flex items-start gap-1">
                            <span className="text-slate-300 flex-shrink-0 mt-0.5">•</span>{ev}
                          </li>
                        ))}
                      </ul>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Skill level table (resume only) */}
          {skillTable.length > 0 && (
            <div data-testid="v4-skill-table">
              <h3 className="text-sm font-semibold text-slate-700 mb-3">Skill Level Breakdown</h3>
              <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm">
                <div className="grid grid-cols-[1.4fr_90px_90px_90px_50px] gap-2 px-4 py-2.5 bg-slate-50 border-b border-slate-200 text-[10px] text-slate-500 font-semibold uppercase tracking-wider">
                  <span>Skill</span><span>Required</span><span>Candidate</span><span>Match</span><span>Score</span>
                </div>
                <div className="max-h-48 overflow-auto divide-y divide-slate-100">
                  {skillTable.map((row) => (
                    <div key={row.skill} className="grid grid-cols-[1.4fr_90px_90px_90px_50px] gap-2 px-4 py-2.5 text-xs items-center hover:bg-slate-50">
                      <span className="text-slate-800 font-medium truncate" title={row.evidence}>{row.skill}</span>
                      <span className="text-slate-500">{row.required || '—'}</span>
                      <span className="text-slate-600">{row.candidate || '—'}</span>
                      <span>
                        <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-semibold border ${matchBadge[row.match_type] || 'bg-slate-100 text-slate-500 border-slate-200'}`}>
                          {row.match_type}
                        </span>
                      </span>
                      <span className={`font-bold ${scoreTextColor(row.score || 0)}`}>{row.score ?? '—'}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Strengths + Concerns */}
          {(strengths.length > 0 || concerns.length > 0) && (
            <div className="grid md:grid-cols-2 gap-4">
              {strengths.length > 0 && (
                <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm" data-testid="v4-strengths">
                  <h3 className="text-xs font-semibold text-emerald-700 uppercase tracking-wider mb-3 flex items-center gap-1.5">
                    <CheckCircle className="w-3.5 h-3.5" /> Strengths
                  </h3>
                  <ul className="space-y-1.5">
                    {strengths.map((s) => (
                      <li key={s} className="flex items-start gap-2 text-xs text-slate-600">
                        <span className="text-emerald-500 flex-shrink-0 mt-0.5">✓</span>{s}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {concerns.length > 0 && (
                <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm" data-testid="v4-concerns">
                  <h3 className="text-xs font-semibold text-red-600 uppercase tracking-wider mb-3 flex items-center gap-1.5">
                    <AlertTriangle className="w-3.5 h-3.5" /> Concerns
                  </h3>
                  <ul className="space-y-1.5">
                    {concerns.map((c) => (
                      <li key={c} className="flex items-start gap-2 text-xs text-slate-600">
                        <span className="text-red-400 flex-shrink-0 mt-0.5">!</span>{c}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          {/* Interview focus */}
          {intFocus.length > 0 && (
            <div data-testid="v4-interview-focus">
              <h3 className="text-xs font-semibold text-[#2563eb] uppercase tracking-wider mb-3">Interview Focus Areas</h3>
              <div className="grid md:grid-cols-2 gap-2">
                {intFocus.map((f, i) => (
                  <div key={f} className="bg-blue-50 border border-blue-100 rounded-lg px-3 py-2 text-xs text-[#2563eb] flex items-start gap-2">
                    <span className="font-bold text-blue-300 flex-shrink-0">{i + 1}.</span>{f}
                  </div>
                ))}
              </div>
            </div>
          )}

        </div>
      </div>
    </div>
  );
}
